import { csrf } from './csrf'
import { objectToFormData } from './object-to-form-data'
import { objectToJsonString } from './object-to-json-string'
import { ApiError, messageForError, readErrorPayload } from './utils/api-error'
import {
  reportAgentSupportLock,
  SUPPORT_SESSION_LOCKED_STATUS,
} from '@/lib/agent-support-lock'

// These two predate apiRequest and are still the only write path for the
// employee survey. They throw ApiError so a caller can read response.status;
// without it a support-session lock would surface as an untyped Error, or as a
// TypeError when the body carries no errors array.
const throwForResponse = async (
  response: Response,
  errorPrefix: string
): Promise<never> => {
  const errorData = await readErrorPayload(response)

  if (response.status === SUPPORT_SESSION_LOCKED_STATUS) {
    reportAgentSupportLock()
  }

  throw new ApiError(
    messageForError(errorData, response, errorPrefix),
    response.status,
    response.statusText
  )
}

export const defaultHeaders = {
  'X-CSRF-Token': csrf,
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

export const submitJsonData = async <
  T extends Record<string, any>,
  R = Response,
>(
  url: string,
  method: HttpMethod,
  data: T
): Promise<R> => {
  const body = objectToJsonString(data)

  let response = await fetch(url, {
    method,
    headers: defaultHeaders,
    body,
  })

  if (!response.ok) {
    await throwForResponse(response, 'Failed to submit data')
  }

  response = await response.json()
  return response as R
}

export const submitFormData = async <
  T extends Record<string, any>,
  R = Response,
>(
  url: string,
  method: HttpMethod,
  data: T,
  namespace: string
): Promise<R> => {
  const body = objectToFormData({ obj: data, namespace })

  let response = await fetch(url, {
    method,
    headers: {
      'X-CSRF-Token': csrf,
    },
    body,
  })

  if (!response.ok) {
    await throwForResponse(response, 'Failed to submit form')
  }

  response = await response.json()
  return response as R
}

export type SubmitMethod = 'POST' | 'PATCH'
